<script setup>
import { computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessageBox } from 'element-plus';
import { SwitchButton } from '@element-plus/icons-vue';
import MenuVue from './components/Menu.vue';
import { useUserStore } from '@/store/user';
import { useConfigStore } from '@/store/config';
import { useMenuStore } from '@/store/menu';
import searchVue from '@/components/Search/Search.vue';
import AiChat from './components/AiChat.vue';
const userStore = useUserStore();
const configStore = useConfigStore();
const menuStore = useMenuStore();
// 用户信息
const userInfo = computed(() => userStore.info);
const config = computed(() => configStore.config);
const menus = computed(() => menuStore.menus);
const activeMenu = computed(() => menuStore.activeMenu);
// 路由实例
const router = useRouter();

// 处理下拉菜单命令
const handleCommand = (command) => {
  switch (command) {
    case 'logout':
      handleLogout();
      break;
    default:
      break;
  }
};

// 处理退出登录
const handleLogout = () => {
  ElMessageBox.confirm('确定要退出登录吗？', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning',
  }).then(async () => {
    try {
      await userStore.logout();
    } catch (err) {
      console.error('退出登录请求失败:', err);
    } finally {
      router.push('/login');
    }
  });
};
watch(
  () => menuStore.menus,
  (menus = []) => {},
);

const projectChange = (projectName) => {
  menuStore.setProject(projectName);
};
const title = computed(() => (menuStore.projectName === 'stellar-ui' ? 'Stellar UI' : 'Stellar Plus'));

const githubUrl = computed(() => {
  const projectName = menuStore.projectName;
  return `https://github.com/wuhanshuzhiyun/stellar-ui${projectName === 'stellar-ui-plus' ? '-plus' : ''}`;
});
const giteeUrl = computed(() => {
  const projectName = menuStore.projectName;
  return `https://gitee.com/wuhanshuzhiyun/stellar-ui${projectName === 'stellar-ui-plus' ? '-plus' : ''}`;
});
</script>
<template>
  <div class="layouts-root">
    <!-- 顶部导航栏 -->
    <el-header class="layout-header">
      <div class="header-left">
        <div class="logo">
          <img :src="config.logo" alt="Logo" class="logo-img" />
          <span class="logo-text">{{ title }}</span>
        </div>
      </div>

      <div class="header-right">
        <searchVue style="margin-right: 20px" />
        <div class="right-menus">
          <div class="menu-item" v-for="menu in menus" :key="menu.id" :class="{ active: activeMenu === menu.name }" @click="menuStore.setActiveMenu(menu.name)">
            <div class="menu-name">{{ menu.name }}</div>
            <div class="menu-active"></div>
          </div>
        </div>
        <el-select style="width: 120px; margin-left: 20px" :model-value="menuStore.projectName" @change="projectChange">
          <el-option v-for="item in menuStore.projects" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
        <el-dropdown @command="handleCommand" v-if="userInfo.id">
          <div class="user-info">
            <el-avatar :size="30" icon="User" :src="userInfo.avatar_url" />
          </div>

          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="logout" divided>
                <el-icon><SwitchButton /></el-icon>
                退出登录
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
        <div class="gits">
          <a target="_blank" :href="githubUrl" title="github" class="link-item" style="background-image: url('https://image.whzb.com/chain/StellarUI/image/github.png')"></a>
          <a target="_blank" :href="giteeUrl" title="gitee" class="link-item" style="background-image: url('https://image.whzb.com/chain/StellarUI/image/gitee.png')"></a>
        </div>
      </div>
    </el-header>

    <!-- 主要内容区域 -->
    <div class="layout-main">
      <div class="layout-menu">
        <MenuVue />
      </div>
      <el-main class="layout-content">
        <router-view />
      </el-main>
    </div>

    <AiChat />
  </div>
</template>

<style lang="scss" scoped>
.layouts-root {
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.layout-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
  background-color: #ffffff;
  box-shadow: 0 2px 4px 0 #eee;
  padding: 0 20px 0 40px;
  z-index: 1000;
}

.header-left {
  display: flex;
  align-items: center;

  .logo {
    display: flex;
    align-items: center;

    .logo-img {
      height: 50px;
      width: 50px;
    }

    .logo-text {
      font-size: 28px;
      font-weight: bold;
      color: #333;
    }
  }
}

.header-right {
  display: flex;
  align-items: center;
  .right-menus {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-left: 40px;
    .menu-item {
      height: 64px;
      cursor: pointer;
      position: relative;
      padding: 0 10px;
      .menu-name {
        color: #333;
        font-size: 14px;
        font-weight: bold;
        line-height: 60px;
      }
      &:hover .menu-name {
        color: var(--root-color);
      }
      &.active .menu-active {
        display: block;
      }
      .menu-active {
        position: absolute;
        width: 100%;
        height: 2px;
        background-color: var(--root-color);
        display: none;
        bottom: 0;
        left: 0;
      }
    }
  }

  .user-info {
    display: flex;
    align-items: center;
    cursor: pointer;
    margin-left: 20px;
  }
  .gits {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-left: 20px;
    .link-item {
      width: 26px;
      height: 26px;
      vertical-align: middle;
      background-repeat: no-repeat;
      background-size: 26px;
      display: inline-block;
      line-height: 64px;
      background-position: 0 -51px;
      color: #ccc;
    }
  }
}

.layout-main {
  width: 100%;
  height: calc(100% - 64px);
  position: relative;
  display: flex;
  align-items: flex-start;
  overflow: hidden;
  .layout-menu {
    width: var(--pc-nav-width);
    height: 100%;
    overflow-y: auto;
    background-color: #fff;
    border-right: 1px solid var(--box-shadow);
  }
  .layout-content {
    width: calc(100% - var(--pc-nav-width));
    height: 100%;
    padding: 0;
    background-color: #fff;
  }
}
</style>
